import { useContext, useEffect, useState, useRef } from "react";
import { useBreadcrumb } from "hooks/useBreadcrumb";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { ClaimsOverview } from "./claims/claims-overview";
import { AgentData } from "context/Agent/DataProvider";
import { getDisabilityClaims } from "service/policy/getDisabilityClaims";

import EverlakeLoadingOverlay from "components/General/EverlakeLoadingOverlay";
import AutoCompleteComboBox from "components/General/AutoCompleteComboBox";

// Helper function to add dateReceived field
const addDateReceived = (claims) => {
  return claims.map((claim) => {
    return {
      ...claim,
      dateReceived:
        claim.claimType === "DIS"
          ? claim.dateReceived
          : claim.dateOfNotification,
    };
  });
};

// Helper function to set policy options
const setPolicyOptions = (agentPolicies, policy) => {
  const currentPolicy = agentPolicies?.find(
    (p) => p.contractID === policy?.contractID
  );
  return {
    value: policy?.oldPolicy,
    displayAs: `${policy?.oldPolicy}: ${currentPolicy?.insuredFullName}`,
  };
};

export default function AgentClaims() {
  const { t } = useTranslation();
  const { agentClaims, agentPolicies } = useContext(AgentData);
  const navigate = useNavigate();

  const [claimsWithDate, setClaimsWithDate] = useState([]);
  const [claimsList, setClaimsList] = useState([]);
  const [policyFilterOptions, setPolicyFilterOptions] = useState([
    { value: "all", displayAs: "Policy Search" },
  ]);
  const [selectedPolicyId, setSelectedPolicyId] = useState({
    value: "all",
    displayAs: "Policy Search",
  });
  const [isLoading, setIsLoading] = useState(true);
  const policyFetchedRef = useRef(false);

  // Function to filter claims based on selected policy
  const filterClaims = (policyId) => {
    const updatedClaims = claimsWithDate.filter(
      (claim) => claim?.oldPolicy === policyId
    );
    setClaimsList(policyId === "all" ? claimsWithDate : updatedClaims);
  };

  // Function to get distinct policies and set filter options
  const getPolicies = () => {
    const distinctClaims = [
      ...new Map(
        claimsWithDate.map((claim) => [claim["oldPolicy"], claim])
      ).values(),
    ];
    const policySelectionArray = distinctClaims.map((policy) =>
      setPolicyOptions(agentPolicies, policy)
    );
    setPolicyFilterOptions([
      { value: "all", displayAs: "Policy Search" },
      ...policySelectionArray,
    ]);
  };

  useEffect(() => {
    if (agentPolicies?.length > 0 && !policyFetchedRef.current) {
      policyFetchedRef.current = true;
      getDisabilityClaims(agentPolicies)
        .then((data) => {
          const combinedClaims = addDateReceived([...agentClaims, ...data]);
          if (combinedClaims.length === 1) {
            setSelectedPolicyId({
              value: `${combinedClaims[0]?.oldPolicy}`,
            });
          }
          setClaimsWithDate(combinedClaims);
          setClaimsList(combinedClaims);
          setIsLoading(false);
        })
        .catch((error) => {
          console.error("Something wrong.", error);
          setIsLoading(false);
        });
    }
  }, [agentClaims, agentPolicies]);

  useEffect(() => {
    filterClaims(selectedPolicyId?.value);
  }, [selectedPolicyId, claimsWithDate]);

  useEffect(() => {
    getPolicies();
  }, [claimsWithDate]);

  useBreadcrumb([
    { name: "Home", url: "/dashboard" },
    { name: "Claims", url: "#" },
  ]);

  return (
    <>
      <div className="container mb-8">
        <div className="flex justify-between">
          <div>
            <h1>{t("CustomerClaims-claimsTitle")}</h1>
          </div>
          <button
            className="ui-btn-primary mb-6 flex"
            onClick={() => navigate("/services-claims/claims/file-a-claim")}
          >
            <svg
              className="ui-icon w-4 mt-1 mr-2"
              focusable="false"
              aria-hidden="true"
              role="presentation"
            >
              <use href={"#ui-icon-edit"}></use>
            </svg>
            <span>{t("CustomerClaims-button")}</span>
          </button>
        </div>
        <hr />
      </div>
      <EverlakeLoadingOverlay
        isLoading={isLoading}
        loadingText={t("AgentClaims-loading")}
      >
        <div className="container ui-component-spacing mb-8">
          <div className="relative mt-1">
            <div className="flex justify-between">
              <div className="relative w-[40%]">
                <AutoCompleteComboBox
                  labelName="Policy Search"
                  inputPlaceholderText={"Select Contract"}
                  options={policyFilterOptions}
                  invalidMessage={t("AlertsCorrespondence-input-invalid-msg-1")}
                  selectedOption={selectedPolicyId}
                  setSelectedOption={setSelectedPolicyId}
                />
              </div>
            </div>
          </div>
          <ClaimsOverview data={claimsList} type={"claims"} />
        </div>

        <div className="relative container ui-component-spacing lg:pb-[4.5rem]"></div>
      </EverlakeLoadingOverlay>
    </>
  );
}
